import { getSubscriptionCategory } from './utils';
import type { MonthlyCount, SubscriptionListRow, SubscriptionType } from '../types/types';
import dayjs from 'dayjs';

// Helper constant for month names
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

export type SubscriptionStatusLabel = 'active' | 'ending_soon' | 'expired';

const SUBSCRIPTION_TYPES: SubscriptionType[] = ['E_MAX', 'ELEGANCE', 'WATER_ONLY', 'SHORT_RENTAL'];

/**
 * REQ-8: the SubscriptionsPage's own status badge — display-only. The API's own `status` filter
 * (`SubscriptionStatusFilter`, apps/api/README.md § API Conventions) matches these same
 * thresholds server-side, but the row's "ends in Nd"/"Nd overdue" label still needs the exact day
 * count, which the list response doesn't carry a precomputed version of.
 */
export const getSubscriptionStatus = (
  endDate: string
): { status: SubscriptionStatusLabel; days: number } => {
  const end = dayjs(endDate).startOf('day');
  const today = dayjs().startOf('day');
  const days = end.diff(today, 'day');
  if (days < 0) return { status: 'expired', days: Math.abs(days) };
  if (days <= 30) return { status: 'ending_soon', days };
  return { status: 'active', days };
};

/**
 * REQ-8 (SHARED_CONTRACTS round 2 "Dashboard totals"): active-subscription-by-type counts --
 * `activeSubscriptionsByType` already carries only active subscriptions, aggregated by the
 * database and keyed by the full `SubscriptionType` enum; a type with no active subscription is
 * absent from the map, read here as zero. Still folds E_MAX+ELEGANCE into "yearly" client-side,
 * exactly as the old all-customers computation did.
 */
export const getActiveSubscriptionsByCategory = (
  activeSubscriptionsByType: Partial<Record<SubscriptionType, number>>
): {
  yearly: number;
  waterOnly: number;
  shortTerm: number;
} => {
  let yearly = 0;
  let waterOnly = 0;
  let shortTerm = 0;

  for (const type of SUBSCRIPTION_TYPES) {
    const count = activeSubscriptionsByType[type] ?? 0;
    const category = getSubscriptionCategory(type);
    if (category === 'yearly') yearly += count;
    else if (category === 'water_only') waterOnly += count;
    else shortTerm += count;
  }

  return { yearly, waterOnly, shortTerm };
};

/**
 * REQ-8: revenue breakdown by category, same source (`activeSubscriptionRevenueByType`) and
 * folding as {@link getActiveSubscriptionsByCategory}.
 */
export const getRevenueByCategory = (
  activeSubscriptionRevenueByType: Partial<Record<SubscriptionType, number>>
): {
  yearly: number;
  waterOnly: number;
  shortTerm: number;
  total: number;
} => {
  let yearly = 0;
  let waterOnly = 0;
  let shortTerm = 0;

  for (const type of SUBSCRIPTION_TYPES) {
    const revenue = activeSubscriptionRevenueByType[type] ?? 0;
    const category = getSubscriptionCategory(type);
    if (category === 'yearly') yearly += revenue;
    else if (category === 'water_only') waterOnly += revenue;
    else shortTerm += revenue;
  }

  return { yearly, waterOnly, shortTerm, total: yearly + waterOnly + shortTerm };
};

/**
 * Upcoming action item (renewal, pickup, etc.) — the Dashboard's own shape for one row of
 * GET /api/v1/subscriptions/upcoming-actions (REQ-8).
 */
export type UpcomingAction = {
  id: string;
  customerId: string;
  customerName: string;
  address: string;
  endDate: string;
  daysUntil: number;
  type: SubscriptionType;
  actionType: 'renewal' | 'pickup' | 'bottle_pickup';
};

/**
 * REQ-8: `actionType` is derived client-side from `type`, as the UI already did before this
 * endpoint existed — the API pre-filters to active subscriptions ending in the next 30 days, so
 * every row here is already "upcoming" by construction.
 */
export const toUpcomingAction = (row: SubscriptionListRow): UpcomingAction => {
  const category = getSubscriptionCategory(row.type);
  const actionType: UpcomingAction['actionType'] =
    category === 'short_term' ? 'pickup' : category === 'water_only' ? 'bottle_pickup' : 'renewal';

  return {
    id: row.id,
    customerId: row.customerId,
    customerName: row.customerName,
    address: `${row.street}, ${row.city}`,
    endDate: row.endDate,
    daysUntil: dayjs(row.endDate).startOf('day').diff(dayjs().startOf('day'), 'day'),
    type: row.type,
    actionType
  };
};

/**
 * Monthly data point for charts
 */
export type MonthlyDataPoint = {
  year: number;
  month: number;
  monthName: string;
  count: number;
};

type YearMonthCount = { year: number; month: number; count: number };

/**
 * apps/api's `MonthlyCount.month` is 1-based (1=January...12=December, `java.time.Month`'s own
 * convention); every other month index in this file is 0-based (`MONTH_NAMES`, `dayjs().month()`)
 * -- converted once here, at ingest, so nothing downstream has to remember which convention it's
 * holding.
 */
const toZeroBasedYearMonth = (counts: MonthlyCount[]): Map<string, YearMonthCount> => {
  const byYearMonth = new Map<string, YearMonthCount>();
  for (const { year, month, count } of counts) {
    byYearMonth.set(`${year}-${month}`, { year, month: month - 1, count });
  }
  return byYearMonth;
};

// Group by year and create monthly data points, months sorted within each year
const groupByYear = (
  countsByYearMonth: Map<string, YearMonthCount>
): Map<number, MonthlyDataPoint[]> => {
  const dataByYear = new Map<number, MonthlyDataPoint[]>();

  for (const { year, month, count } of countsByYearMonth.values()) {
    const months = dataByYear.get(year) ?? [];
    months.push({ year, month, monthName: MONTH_NAMES[month] ?? '', count });
    dataByYear.set(year, months);
  }

  for (const months of dataByYear.values()) {
    months.sort((a, b) => a.month - b.month);
  }

  return dataByYear;
};

/**
 * REQ-8: new subscriptions per month -- apps/api's `newSubscriptionsPerMonth` already buckets
 * every subscription (active or not), sorted ascending by (year, month); this only re-groups by
 * year for the chart's own year selector.
 */
export const calculateNewSubscriptionsPerMonth = (
  newSubscriptionsPerMonth: MonthlyCount[]
): Map<number, MonthlyDataPoint[]> => groupByYear(toZeroBasedYearMonth(newSubscriptionsPerMonth));

/**
 * REQ-8: new clients per month -- apps/api's `newCustomersPerMonth` buckets by the customer's own
 * `createdAt` (the domain model's design commit, docs/DATA_MODEL.md), not the customer's earliest
 * subscription start date the old all-customers computation used.
 */
export const calculateNewClientsPerMonth = (
  newCustomersPerMonth: MonthlyCount[]
): Map<number, MonthlyDataPoint[]> => groupByYear(toZeroBasedYearMonth(newCustomersPerMonth));
